import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowRight } from 'lucide-react';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import { useSEO } from '../hooks/useSEO';
import { BLOG_POSTS, BLOG_CATEGORIES, formatDate, type BlogCategory } from '../data/blogPosts';

const SAMPLE_VOYAGES = [
  {
    id: 'nsb',
    title: 'New Smyrna: Salt & Solitude',
    location: 'Florida, USA',
    image: 'https://images.unsplash.com/photo-1506953823976-52e1fdc0149a?q=80&w=1600&auto=format&fit=crop',
    description: 'A curated retreat to the Atlantic\'s hidden artistic enclave. Where Brutalist architecture meets wild dunes, and the surf culture is steeped in quiet history.',
    tags: ['Coastal Americana', 'Surf Noir'],
  },
  {
    id: 'kyoto',
    title: 'Kyoto After Dark',
    location: 'Japan',
    image: 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?q=80&w=1200&auto=format&fit=crop',
    description: 'An exploration of the shadow world of the ancient capital. From hidden whiskey bars in Pontocho to moonlight meditations in private Zen gardens.',
    tags: ['Cultural Immersion', 'Vogue Minimal'],
  },
  {
    id: 'amalfi',
    title: 'Amalfi Coast by Sea',
    location: 'Italy',
    image: 'https://images.unsplash.com/photo-1520175480921-4edfa2983e0f?q=80&w=1600&auto=format&fit=crop',
    description: 'La Dolce Vita reimagined. A sea-centric perspective of the coast, avoiding the roads entirely. Vintage wooden boats, lemon groves, and cliffside grandeur.',
    tags: ['Epicurean Discovery', 'Golden Hour'],
  },
];

const JournalPage: React.FC = () => {
  useSEO({
    title: 'The Aura Journal — Luxury Travel Insights & Curated Expeditions',
    description:
      'The Aura Traveler Journal: editorial perspectives on luxury travel design, AI in travel agencies, destination intelligence, and curated sample expeditions from around the world.',
    canonical: 'https://www.auratraveler.com/journal',
    breadcrumbs: [
      { name: 'Home', url: 'https://www.auratraveler.com/' },
      { name: 'Journal', url: 'https://www.auratraveler.com/journal' },
    ],
  });

  const [activeCategory, setActiveCategory] = useState<BlogCategory | 'All'>('All');

  const filteredPosts = activeCategory === 'All'
    ? BLOG_POSTS
    : BLOG_POSTS.filter(p => p.category === activeCategory);

  const featuredPost = filteredPosts[0];
  const remainingPosts = filteredPosts.slice(1);

  return (
    <div className="min-h-screen bg-charcoal text-white font-sans selection:bg-gold/30 selection:text-white">
      <SiteNav />

      <main>
      {/* Hero */}
      <section className="pt-40 pb-20 px-4 sm:px-6 md:px-12 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
          >
            <span className="font-sans text-[10px] uppercase tracking-[0.5em] text-gold/60 mb-8 block">
              The Journal
            </span>
            <h1 className="font-display text-4xl sm:text-6xl md:text-8xl text-white leading-[0.92] tracking-tighter font-bold mb-8">
              Curated{' '}
              <span className="font-serif italic font-medium text-gold">Intelligence</span>
            </h1>
            <p className="font-serif text-lg text-white/40 max-w-2xl leading-relaxed">
              Industry insights, destination intelligence, and the strategies redefining luxury travel in 2026.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="px-4 sm:px-6 md:px-12 lg:px-16 border-t border-white/5 pt-10 pb-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setActiveCategory('All')}
              className={`px-5 py-2.5 rounded-full font-sans text-[10px] uppercase tracking-[0.2em] font-bold transition-all duration-300 min-h-[40px] ${
                activeCategory === 'All'
                  ? 'bg-white text-charcoal'
                  : 'bg-transparent border border-white/15 text-white/50 hover:border-white/30 hover:text-white/80'
              }`}
            >
              All
            </button>
            {BLOG_CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2.5 rounded-full font-sans text-[10px] uppercase tracking-[0.2em] font-bold transition-all duration-300 min-h-[40px] ${
                  activeCategory === cat
                    ? 'bg-white text-charcoal'
                    : 'bg-transparent border border-white/15 text-white/50 hover:border-white/30 hover:text-white/80'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Blog Post */}
      <section className="py-12 px-4 sm:px-6 md:px-12 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <Link
            to={`/journal/${featuredPost.slug}`}
            className="group block"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-white/5 hover:border-gold/20 transition-all duration-700">
              <div className="aspect-[4/3] lg:aspect-auto overflow-hidden">
                <img
                  src={featuredPost.featuredImage}
                  alt={featuredPost.featuredImageAlt}
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement!.classList.add('bg-gradient-to-br', 'from-[#1a1a1a]', 'to-[#2a2a2a]'); }}
                  className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700"
                />
              </div>
              <div className="bg-[#141414] p-10 md:p-16 flex flex-col justify-center">
                <div className="flex items-center gap-4 mb-6">
                  <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/50">
                    Featured
                  </span>
                  <span className="w-8 h-px bg-white/20" />
                  <span className="font-serif text-[11px] italic text-white/30">
                    {formatDate(featuredPost.date)}
                  </span>
                </div>
                <h2 className="font-display text-2xl md:text-3xl text-white mb-6 tracking-tight group-hover:text-gold transition-colors duration-500 leading-tight">
                  {featuredPost.title}
                </h2>
                <p className="font-serif text-base text-white/60 leading-relaxed mb-8 line-clamp-3">
                  {featuredPost.excerpt}
                </p>
                <div className="flex items-center gap-3">
                  <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-gold">
                    Read Article
                  </span>
                  <ArrowRight className="w-4 h-4 text-gold group-hover:translate-x-2 transition-transform duration-500" />
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* More Blog Posts */}
      <section className="py-20 px-4 sm:px-6 md:px-12 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
            {remainingPosts.map((post, i) => (
              <motion.div
                key={post.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
              >
                <Link to={`/journal/${post.slug}`} className="group block">
                  <div className="border border-white/5 hover:border-gold/20 transition-all duration-700 bg-[#141414]">
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={post.featuredImage}
                        alt={post.featuredImageAlt}
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement!.classList.add('bg-gradient-to-br', 'from-[#1a1a1a]', 'to-[#2a2a2a]'); }}
                        className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700"
                      />
                    </div>
                    <div className="p-8 md:p-10">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="font-serif text-[11px] italic text-white/30">
                          {formatDate(post.date)}
                        </span>
                        <span className="w-4 h-px bg-white/10" />
                        <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-gold/50">
                          {post.tags[0]}
                        </span>
                      </div>
                      <h3 className="font-display text-xl text-white mb-4 tracking-tight group-hover:text-gold transition-colors duration-500 leading-tight">
                        {post.title}
                      </h3>
                      <p className="font-serif text-sm text-white/50 leading-relaxed line-clamp-2 mb-6">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-gold/70 group-hover:text-gold transition-colors duration-300">
                          Read
                        </span>
                        <ArrowRight className="w-3.5 h-3.5 text-gold/70 group-hover:text-gold group-hover:translate-x-1 transition-all duration-300" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-link to Field Notes */}
      <section className="py-16 px-4 sm:px-6 md:px-12 lg:px-16 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 p-8 md:p-12 border border-white/5 bg-[#141414] hover:border-gold/20 transition-all duration-500">
            <div>
              <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/50 mb-3 block">From the Field</span>
              <h3 className="font-display text-xl md:text-2xl text-white tracking-tight mb-2">Field Notes</h3>
              <p className="font-serif text-sm text-white/50 leading-relaxed max-w-lg">
                Destination deep-dives, cultural dispatches, and the art of extraordinary travel — editorial stories from the world's most remarkable places.
              </p>
            </div>
            <Link
              to="/field-notes"
              className="flex items-center gap-3 px-6 py-3 border border-white/10 text-white text-[10px] font-bold uppercase tracking-[0.2em] rounded-full hover:border-gold hover:text-gold transition-all duration-500 flex-shrink-0"
            >
              Explore Field Notes
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="border-t border-white/5 mx-4 sm:mx-6 md:mx-12 lg:mx-16" />

      {/* Sample Voyages */}
      <section className="py-24 px-4 sm:px-6 md:px-12 lg:px-16 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/50 mb-6 block">
              Sample Expeditions
            </span>
            <h2 className="font-display text-3xl md:text-5xl text-white tracking-tighter">
              Curated Voyages
            </h2>
            <p className="font-serif text-base text-white/40 mt-4 max-w-xl">
              AI-generated itinerary samples — real destinations, real itineraries, real Aura output.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SAMPLE_VOYAGES.map((voyage, i) => (
              <motion.div
                key={voyage.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="group cursor-pointer"
              >
                <div className="aspect-[3/4] overflow-hidden mb-5">
                  <img
                    src={voyage.image}
                    alt={voyage.title}
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement!.classList.add('bg-gradient-to-br', 'from-[#1a1a1a]', 'to-[#2a2a2a]'); }}
                    className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700"
                  />
                </div>
                <div className="flex gap-2 mb-3">
                  {voyage.tags.map((tag) => (
                    <span
                      key={tag}
                      className="font-sans text-[10px] uppercase tracking-[0.3em] text-gold/50 border border-gold/20 px-2 py-0.5"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h3 className="font-display text-xl text-white tracking-tight mb-2 group-hover:text-gold transition-colors duration-300">
                  {voyage.title}
                </h3>
                <p className="font-serif text-[11px] text-white/40 uppercase tracking-[0.2em] mb-3">
                  {voyage.location}
                </p>
                <p className="font-serif text-sm text-white/50 leading-relaxed line-clamp-2">
                  {voyage.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter / CTA */}
      <section className="py-24 px-4 sm:px-6 md:px-12 lg:px-16 bg-charcoal border-t border-white/5 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-display text-3xl md:text-5xl text-white tracking-tighter mb-6">
            Design your own{' '}
            <span className="font-serif italic text-gold">expedition</span>
          </h2>
          <p className="font-serif text-lg text-white/40 mb-10">
            Create a magazine-quality travel itinerary in under 90 seconds.
          </p>
          <Link
            to="/"
            className="inline-block px-12 py-5 bg-gold text-black font-sans text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-white transition-all duration-700 rounded-full"
          >
            Start Planning
          </Link>
        </div>
      </section>

      </main>

      <SiteFooter />
    </div>
  );
};

export default JournalPage;
